import {
  Button,
  FormControl,
  FormLabel,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useState } from "react";
import { FaCheck } from "react-icons/fa";
import { ContactPersonDto, EmailInviteLanguage, useAddContactPersonMutation } from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { isValidEmail } from "../../../../common/formatting/isValidEmail";
import { PhoneNumberInput } from "../../../../common/input/PhoneNumberInput";
import { phoneNumberDtoToInput, phoneNumberInputToDto } from "../../../../common/input/phoneNumberMapping";
import { Explanation } from "../../../../common/support/Explanation/Explanation";
import { useBaseData } from "../../../../common/useBaseData";
import { ContactSubject } from "./contactSubject";

const initContactPerson = (): ContactPersonDto => ({
  name: "",
  email: "",
  language: EmailInviteLanguage.No,
  title: "",
});

const subjectKey = (subject: ContactSubject) => `${subject.subjectType}:${subject.subjectId}`;

interface Props {
  subjects: ContactSubject[];
  onClose: () => void;
}

export const ContactPersonAddForm = ({ subjects, onClose }: Props) => {
  const baseData = useBaseData();
  const displayer = useApiError();
  const [addContactPerson, { isLoading }] = useAddContactPersonMutation();

  const [selectedSubjectKey, setSelectedSubjectKey] = useState(subjects[0] ? subjectKey(subjects[0]) : "");
  const [contactPerson, setContactPerson] = useState<ContactPersonDto>(initContactPerson());

  const selectedSubject = subjects.find((subject) => subjectKey(subject) === selectedSubjectKey);

  const isValid = !!contactPerson.name.trim() && isValidEmail(contactPerson.email) && !!selectedSubject;

  const handleSave = async () => {
    if (!selectedSubject) return;
    try {
      await addContactPerson({
        subjectType: selectedSubject.subjectType,
        subjectId: selectedSubject.subjectId,
        addContactPersonRequest: { contact: contactPerson },
      }).unwrap();
      onClose();
    } catch (e) {
      displayer.trigger(e);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Add contact person")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {subjects.length > 1 && (
            <FormControl pb="2">
              <FormLabel fontSize="sm">{t("Company")}</FormLabel>
              <Select
                size="sm"
                rounded="md"
                backgroundColor="smBackground"
                value={selectedSubjectKey}
                onChange={(e) => setSelectedSubjectKey(e.target.value)}
              >
                {subjects.map((subject) => (
                  <option key={subjectKey(subject)} value={subjectKey(subject)}>
                    {subject.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl>
            <FormLabel fontSize="sm">{t("Name")}</FormLabel>
            <Input
              autoComplete="off"
              size="sm"
              rounded="md"
              backgroundColor="smBackground"
              value={contactPerson.name}
              onChange={(e) => setContactPerson({ ...contactPerson, name: e.target.value })}
            />
          </FormControl>
          <FormControl pt="2">
            <FormLabel fontSize="sm">{t("Title")}</FormLabel>
            <Input
              autoComplete="off"
              size="sm"
              rounded="md"
              backgroundColor="smBackground"
              value={contactPerson.title ?? ""}
              onChange={(e) => setContactPerson({ ...contactPerson, title: e.target.value })}
            />
          </FormControl>
          <FormControl pt="2">
            <FormLabel fontSize="sm">{t("Email")}</FormLabel>
            <Input
              autoComplete="off"
              size="sm"
              rounded="md"
              backgroundColor="smBackground"
              value={contactPerson.email}
              onChange={(e) => setContactPerson({ ...contactPerson, email: e.target.value })}
            />
          </FormControl>
          <FormControl pt="2">
            <FormLabel fontSize="sm">{t("Phone number")}</FormLabel>
            <PhoneNumberInput
              size="sm"
              value={phoneNumberDtoToInput(contactPerson.phoneNumber, baseData.callingCodes)}
              onChange={(phoneNumber) =>
                setContactPerson({ ...contactPerson, phoneNumber: phoneNumberInputToDto(phoneNumber) })
              }
            />
          </FormControl>
          <FormControl pt="2">
            <FormLabel fontSize="sm">{t("Invitation language")}</FormLabel>
            <Select
              value={contactPerson.language}
              size="sm"
              rounded="md"
              backgroundColor="smBackground"
              onChange={(e) => setContactPerson({ ...contactPerson, language: e.target.value as EmailInviteLanguage })}
            >
              <option value="No">{t("Norwegian")}</option>
              <option value="En">{t("English")}</option>
            </Select>
          </FormControl>
        </ModalBody>
        <ModalFooter columnGap={2}>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Explanation text={t("Make sure at least email and name is valid")} enabled={!isValid}>
            <Button
              colorScheme="teal"
              size="sm"
              variant="outline"
              rightIcon={<Icon as={FaCheck} />}
              isLoading={isLoading}
              isDisabled={!isValid}
              onClick={handleSave}
            >
              {t("Add contact person")}
            </Button>
          </Explanation>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
